import React from "react";
import { useField, useFormikContext } from "formik";
import { useCopies } from "shared/hooks/useCopies";
import { FormFieldOption, NestedFormCheckboxFieldGroupConfig } from "../types";
import classNames from "classnames";
import FormGroup from "../formGroup";
import Size from "shared/enums/Size";
import FormLabel from "../formLabel";
import Button from "shared/components/button";
import Variant from "shared/enums/Variant";
import ErrorField from "../errorField";
import FieldOptionViewType from "shared/form/renderer/enums/FieldOptionViewType";
import CheckBox from "shared/form/core/checkbox";
import Input from "shared/form/core/input";
import flatten from "lodash/flatten";
import { isEmpty } from "shared/utils";
import FormOverwriteField from "../overwriteField";
import { ConflictTagType, ConflictType } from "shared/form/renderer/enums/Conflict";
import Icon from "assets/icons";
import xor from "lodash/xor";
import { ConflictTag } from "../overwriteField/components/conflictTag";
import { isArray } from "lodash";
import { ValidateNestedMultiSelectFormOption } from "../utils/resetOptionsForInvalidSelection";

function FormNestedCheckboxFieldGroup({
  fullWidth = true,
  size = Size.MEDIUM,
  ...props
}: NestedFormCheckboxFieldGroupConfig) {
  const copies = useCopies();
  const defaultSelectAllText = copies.get("select.all");
  const defaultClearAllText = copies.get("clear.all");
  const { setFieldTouched, setFieldValue, validateForm, validateOnChange } = useFormikContext();
  const [field, meta] = useField({
    name: props.name,
    validate: props.validate
  });
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [openItem, setOpenItem] = React.useState<number>();
  const [openItems, setOpenItems] = React.useState<Array<number> | undefined>();

  React.useEffect(() => {
    if (props.conflictValue && !openItems && props.openAccordionsWithConflicts) {
      setOpenItems(
        props.options
          .filter((o: FormFieldOption) =>
            o.childOptions?.find((childOpt: FormFieldOption) => childOpt.customData?.className)
          )
          .map((_, idx) => idx)
      );
    }
  }, [openItems, props.conflictValue, props.openAccordionsWithConflicts, props.options]);

  const isAccordionExpanded = React.useCallback(
    (indexToCheck: number) => {
      return props.conflictValue ? openItems?.includes(indexToCheck) : openItem === indexToCheck;
    },
    [openItem, openItems, props.conflictValue]
  );

  const toggleAccordionState = React.useCallback(
    (indexToToggle: number) => {
      if (props.conflictValue) {
        setOpenItems(prevOpenItems => xor(prevOpenItems, [indexToToggle]));
      } else {
        setOpenItem(openItem === indexToToggle ? undefined : indexToToggle);
      }
    },
    [openItem, props.conflictValue]
  );

  const getFilteredParentOptions = React.useCallback(
    (parentOptions: Array<FormFieldOption>) =>
      parentOptions.filter((parentOption: FormFieldOption) => parentOption.childOptions?.length),
    []
  );

  const filteredOptions = React.useMemo(() => {
    const filteredParentOptions = getFilteredParentOptions(props.options);
    if (!searchTerm.length) return filteredParentOptions;
    const toReturn = filteredParentOptions.map((parentOption: FormFieldOption) => {
      const newChildOptions = parentOption.childOptions?.filter(
        (childOption: FormFieldOption) => isArray(childOption.label) && childOption.label.includes(searchTerm)
      );
      return { ...parentOption, childOptions: newChildOptions || [] };
    });
    return getFilteredParentOptions(toReturn);
  }, [getFilteredParentOptions, props.options, searchTerm]);

  const changeHandler = React.useCallback(
    async (e, value) => {
      if (e.target.checked) {
        await setFieldValue(props.name, [...field.value, value.value], false);
      } else {
        await setFieldValue(
          props.name,
          field.value.filter((a: string) => a !== value.value),
          false
        );
      }
      if (!meta.touched) {
        await setFieldTouched(props.name, true, false);
      }
      validateOnChange && (await validateForm());
    },
    [field.value, props.name, setFieldTouched, setFieldValue, meta.touched, validateForm, validateOnChange]
  );

  const handleSelectAll = React.useCallback(
    async e => {
      e.preventDefault();
      const allOptions = flatten(
        filteredOptions.map((parentOption: FormFieldOption) =>
          parentOption.childOptions?.map((childOption: FormFieldOption) => childOption.value)
        )
      );
      await setFieldValue(props.name, allOptions);
      if (!meta.touched) {
        await setFieldTouched(props.name, true);
      }
    },
    [setFieldValue, props.name, filteredOptions, meta.touched, setFieldTouched]
  );

  const handleClearAll = React.useCallback(
    async e => {
      e.preventDefault();
      await setFieldValue(props.name, []);
      if (!meta.touched) {
        await setFieldTouched(props.name, true);
      }
    },
    [props.name, setFieldTouched, setFieldValue, meta.touched]
  );

  const computedClassNames = React.useMemo(() => {
    return classNames(props.className, {
      checkboxGroup: true,
      nestedCheckboxGroup: true,
      "checkboxGroup--disabled": props.disabled,
      [`checkboxGroup--${size}`]: size,
      [`checkboxGroup--${props.variant}`]: props.variant
    });
  }, [props.className, props.disabled, props.variant, size]);

  const searchTermChangeHandler = React.useCallback(e => {
    setSearchTerm(e.target.value.trim());
  }, []);

  return (
    <React.Fragment>
      <FormGroup fullWidth={fullWidth} indented={props.indented}>
        <div className={computedClassNames}>
          <div className="checkboxGroup_labelWrapper">
            <FormLabel
              label={props.label}
              required={props.required}
              className="checkboxGroup_title"
              infoLabel={props.infoLabel}
            />

            {props.withSelectAll && (
              <div className="checkboxGroup_selectAllActionWrapper">
                <Button
                  onClick={handleSelectAll}
                  label={props.selectAllText || defaultSelectAllText}
                  disabled={props.disabled || filteredOptions.length === 0}
                  variant={Variant.LINK}
                />
                <span>|</span>
                <Button
                  onClick={handleClearAll}
                  label={props.clearAllText || defaultClearAllText}
                  disabled={props.disabled || filteredOptions.length === 0}
                  variant={Variant.LINK}
                />
              </div>
            )}
          </div>
          <ErrorField meta={meta} />
          <div
            className={classNames("checkboxGroup_wrapper", {
              // todo:sk update class name
              hasSingleOption: props.fieldOptionViewType === FieldOptionViewType.SINGLE_COLUMN
            })}>
            {props.searchEnabled && (
              <Input
                name={`${props.name}SearchTerm`}
                value={searchTerm}
                onChange={searchTermChangeHandler}
                disabled={props.disabled}
                placeholder={props.placeholder}
              />
            )}

            {filteredOptions.map((parentOption: FormFieldOption, index: number) => {
              if (!parentOption.childOptions?.length) return null;
              const selectedOptions = parentOption.childOptions.filter(o => field.value?.includes(o.value));
              const removedOptions = parentOption.childOptions.filter(
                o => props.conflictValue?.includes(o.value) && !field.value?.includes(o.value)
              );

              return (
                <React.Fragment key={index}>
                  <div
                    className={classNames("checkboxGroup_accordion", {
                      expanded: isAccordionExpanded(index)
                    })}>
                    <div className="checkboxGroup_accordion_title" onClick={() => toggleAccordionState(index)}>
                      <div className="checkboxGroup_accordion_title_header">
                        <h5>{parentOption.label}</h5>
                        <Icon.ChevronDown />
                      </div>
                      {!isAccordionExpanded(index) && (
                        <div className="checkboxGroup_accordion_title_tags">
                          {/* display untouched options i.e. which were in existing values as well as user had kept them selected */}
                          {selectedOptions
                            .filter(o => !o.customData?.conflictTagType)
                            .map(o => (
                              <span
                                key={o.value}
                                title={typeof o.label === "string" ? o.label : undefined}
                                className={o.customData?.className}>
                                {o.label}
                                <ConflictTag conflictTagType={o.customData?.conflictTagType} />
                              </span>
                            ))}
                          {/* display new values added by user i.e. which were not present in existing values but user has selected */}
                          {selectedOptions
                            .filter(o => o.customData?.conflictTagType === ConflictTagType.NEW_VALUE)
                            .map(o => (
                              <span
                                title={typeof o.label === "string" ? o.label : undefined}
                                className={o.customData?.className}>
                                {o.label}
                                <ConflictTag conflictTagType={o.customData?.conflictTagType} />
                              </span>
                            ))}
                          {/* display removed values by user i.e. which were present in existing values but user has not selected/unchecked */}
                          {removedOptions.map(o => (
                            <span
                              title={typeof o.label === "string" ? o.label : undefined}
                              className={o.customData?.className}>
                              {o.label}
                              <ConflictTag conflictTagType={o.customData?.conflictTagType} />
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div
                      className={classNames(
                        "checkboxGroup_accordion_options slideDownAnimation",
                        isAccordionExpanded(index) ? "show" : "hide"
                      )}>
                      {parentOption.childOptions?.map(item => (
                        <CheckBox
                          name={item.value}
                          checked={field.value?.includes("" + item.value)}
                          onChange={e => changeHandler(e, item)}
                          label={item.label}
                          key={item.value}
                          disabled={props.disabled}
                          size={size}
                          classNameForLabel={item.customData?.className}
                          conflictTagType={item.customData?.conflictTagType}
                        />
                      ))}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
          <ValidateNestedMultiSelectFormOption options={props.options} values={field.value} name={props.name} />
          {!isEmpty(props.conflictValue) && (
            <FormOverwriteField
              forFieldName={props.name}
              disabled={props.disabled}
              conflictType={ConflictType.NESTED_MULTISELECT}
              options={props.options}
            />
          )}
        </div>
      </FormGroup>
    </React.Fragment>
  );
}

export default FormNestedCheckboxFieldGroup;
